import type { AxiosResponse } from 'axios'
import type {
  CartDocumentResponse,
  CartDocumentResult,
  DeleteMultiCartDocument,
  DeleteMultiCartRecipient,
  GetRecipientClientListResponse,
  GetRecipientFolderListResponse,
  MultiCartRecipientsAdd,
  MultiCartsResponse,
  MultiSendCartSortInfo,
  PostMultiCartsParams,
  RecipientClientList,
  RecipientMasterOptions,
  TrustTransactionsMultiParams,
} from './model/multiSendCartModel'
import type { FolderList } from '../model/folderModel'

import { bffHTTP } from '@/utils/http/axios'
import { handleAPIException } from '@/utils/exception'
import { HTTPMethodsEnum } from '@/enums'

enum API_URL {
  MULTI_CARTS = '/multi_carts',
  MULTI_CARTS_COUNT = '/multi_carts/count',
  MULTI_CART_RECIPIENTS = '/multi_cart_recipients',
  RECIPIENTS = '/recipients',
  RECIPIENT_DETAILS = '/recipient_details',
  FOLDERS_OTHER = '/folders/other',
  TRUST_TRANSACTIONS_MULTI_CARTS = '/trust/transactions/multi',
}

/**
 * @description 一括送信カート追加 API
 * @param {object} data
 * @returns {number} 送信カート件数
 */
export async function postMultiCarts(
  data: PostMultiCartsParams
): Promise<boolean> {
  try {
    const response: AxiosResponse<MultiCartsResponse, void> =
      await bffHTTP({
        method: HTTPMethodsEnum.POST,
        url: API_URL.MULTI_CARTS,
        data: data,
      })

    handleAPIException(response)
    return true
  } catch (error) {
    console.log('postMultiCarts', error)
    return false
  }
}

/**
 * @description 一括送信カート件数取得API
 * @returns 一括送信カート件数
 */
export async function getMultiCartsCount(): Promise<number | undefined> {
  try {
    const response: AxiosResponse<MultiCartsResponse, void> = await bffHTTP({
      method: HTTPMethodsEnum.GET,
      url: API_URL.MULTI_CARTS_COUNT,
    })

    handleAPIException(response)
    return response.data.detail.count
  } catch (error) {
    console.log("getMultiCartsCount", error)
  }
}

/**
 * @description 一括送信カート取得API
 * @returns 一括送信カート画面データ
 */
export async function getMultiCarts(params?: MultiSendCartSortInfo): Promise<CartDocumentResult> {
  try {
    const response: AxiosResponse<CartDocumentResponse, null> = await bffHTTP({
      method: HTTPMethodsEnum.GET,
      url: API_URL.MULTI_CARTS,
      params: params
    })

    handleAPIException(response)
    return response.data.detail[0]
  } catch (error) {
    console.log('getMultiCarts error')
    return { cart_document: [], cart_recipient: [] }
  }
}

/**
 * @descripiton 送信先マスタ一覧
 * @returns 送信先選択 送信先マスタ一覧
 */
export async function getRecipientMasterOptions(): Promise<RecipientMasterOptions> {
  try {
    const response: AxiosResponse<{ detail: RecipientMasterOptions }, any> =
      await bffHTTP({
        method: HTTPMethodsEnum.GET,
        url: API_URL.RECIPIENTS,
      })

    handleAPIException(response)
    return response.data.detail
  } catch (error) {
    console.log('getRecipientOptions error', error)
    return []
  }
}

/**
 * @description 送信先詳細一覧取得 API
 * @param RecipientClientId 送信先 ID
 * @returns 送信先詳細一覧
 */
export async function getRecipientClientList(
  RecipientClientId: string | number
): Promise<RecipientClientList> {
  try {
    const response: AxiosResponse<GetRecipientClientListResponse, string | number> =
      await bffHTTP({
        method: HTTPMethodsEnum.GET,
        url: API_URL.RECIPIENT_DETAILS + '/' + `${RecipientClientId.toString()}`,
      })

    handleAPIException(response)
    return response.data.detail
  } catch (error) {
    console.log('getRecipientClientList', error)
    return []
  }
}

/**
 * @description 他社フォルダと送信先取得 API
 * @param corporateNumber 送信先法人番号
 * @returns 送信先フォルダ一覧
 */
export async function getRecipientFolderList(
  corporateNumber: number
): Promise<FolderList> {
  try {
    const response: AxiosResponse<GetRecipientFolderListResponse, void> =
      await bffHTTP({
        method: HTTPMethodsEnum.GET,
        url: API_URL.FOLDERS_OTHER,
        params: { recipient_corporate_number: corporateNumber },
      })

    handleAPIException(response)
    return response.data.detail
  } catch (error) {
    console.log('getRecipientFolderList', error)
    return []
  }
}

/**
 * @description 一括送信カート宛先登録API
 * @param {object} data
 * @returns boolean
 */
export async function postMultiCartRecipients(
  data: MultiCartRecipientsAdd
): Promise<boolean> {
  try {
    const response: AxiosResponse<null, void> =
      await bffHTTP({
        method: HTTPMethodsEnum.POST,
        url: API_URL.MULTI_CART_RECIPIENTS,
        data: data,
      })

    handleAPIException(response)
    return true
  } catch (error) {
    console.log('postMultiCartRecipients', error)
    return false
  }
}

/**
 * @description 一括送信カート明細削除API
 * @param {object} params
 * @returns boolean
 */
export async function deleteMultiSendDocument(
  params: DeleteMultiCartDocument
): Promise<boolean> {
  try {
    const response: AxiosResponse<MultiCartsResponse, void> =
      await bffHTTP({
        method: HTTPMethodsEnum.DELETE,
        url: API_URL.MULTI_CARTS + `/${params.product_id}/${params.document_id}/${params.file_id}`,
        params: params,
      })

    handleAPIException(response)
    return true
  } catch (error) {
    console.log('deleteMultiSendDocument', error)
    return false
  }
}


/**
 * @description 一括送信カート宛先削除API
 * @param {object} params
 * @returns boolean
 */
export async function deleteMultiSendRecipient(
  params: DeleteMultiCartRecipient
): Promise<boolean> {
  try {
    const response: AxiosResponse<MultiCartsResponse, void> =
      await bffHTTP({
        method: HTTPMethodsEnum.DELETE,
        url: API_URL.MULTI_CART_RECIPIENTS + `/${params.recipient_corporate_number}/${params.recipient_user_id}/${params.recipient_folder_id}`,
      })

    handleAPIException(response)
    return true
  } catch (error) {
    console.log('deleteMultiSendRecipient', error)
    return false
  }
}

/**
 * @description 一括送信カートすべてのドキュメント削除API
 * @returns boolean
 */
export async function deleteMultiSendCart(
): Promise<boolean> {
  try {
    const response: AxiosResponse<MultiCartsResponse, void> =
      await bffHTTP({
        method: HTTPMethodsEnum.DELETE,
        url: API_URL.MULTI_CARTS,
      })

    handleAPIException(response)
    return true
  } catch (error) {
    console.log('deleteMultiSendCart', error)
    return false
  }
}

/**
 * @description 一括送信「確認画面へ」ボタン押下後のチェック用API
 * @param {object} params
 * @returns boolean
 */
export async function postTrustTransactionMulti(params: TrustTransactionsMultiParams
): Promise<boolean> {
  try {
    const response: AxiosResponse<null, void> =
      await bffHTTP({
        method: HTTPMethodsEnum.POST,
        url: API_URL.TRUST_TRANSACTIONS_MULTI_CARTS,
        data: params,
      })

    handleAPIException(response)
    return true
  } catch (error) {
    console.log('postTrustTransactionMulti', error)
    return false
  }
}

/**
 * @description 一括送信カート更新API
 * @returns boolean
 */
export async function putMultiSendCart(data: { delete_flag: string }
): Promise<boolean> {
  try {
    const response: AxiosResponse<MultiCartsResponse, void> =
      await bffHTTP({
        method: HTTPMethodsEnum.PUT,
        url: API_URL.MULTI_CARTS,
        data: data
      })

    handleAPIException(response)
    return true
  } catch (error) {
    console.log('putMultiSendCart', error)
    return false
  }
}

/**
 * @description 一括送信カートすべての宛先削除API
 * @returns boolean
 */
export async function deleteMultiCartRecipients(
): Promise<boolean> {
  try {
    const response: AxiosResponse<null, void> =
      await bffHTTP({
        method: HTTPMethodsEnum.DELETE,
        url: API_URL.MULTI_CART_RECIPIENTS,
      })

    handleAPIException(response)
    return true
  } catch (error) {
    console.log('deleteMultiCartRecipients', error)
    return false
  }
}